import type {
  Properties_Insert_Input,
  Property_Transactions_Insert_Input,
} from '@graphql/__generated__/graphql';
import type { RaAddressType } from '@modules/_shared/components/form/RaAddressInput';

export const addressToPropertyData = (
  address: RaAddressType,
  leadProperty?: {
    property_type_id?: string | null;
    main_type?: string | null;
    number_of_rooms?: number | null;
    number_of_bedrooms?: number | null;
    living_surface?: number | null;
    land_surface?: number | null;
    built_surface?: number | null;
    usable_surface?: number | null;
  } | null,
): Partial<Properties_Insert_Input> => {
  return {
    route: address.route,
    street_number: address.street_number,
    locality: address.locality,
    state: address.state,
    country: address.country,
    country_code: address.country_code,
    postcode: address.postcode,
    lat: address.lat,
    lng: address.lng,
    ...(leadProperty
      ? {
          property_type_id: leadProperty.property_type_id,
          number_of_rooms: leadProperty.number_of_rooms,
          number_of_bedrooms: leadProperty.number_of_bedrooms,
          living_surface: leadProperty.living_surface,
          land_surface: leadProperty.land_surface,
          built_surface: leadProperty.built_surface,
          usable_surface: leadProperty.usable_surface,
        }
      : {}),
  };
};

export const isLandProperty = (mainType?: string | null): boolean => {
  return mainType === 'PROP';
};

export const isApartmentProperty = (
  propertyTypeName?: string | null,
  mainType?: string | null,
): boolean => {
  return propertyTypeName?.includes('APPT') === true || mainType === 'APPT';
};

export type TransactionInputData = {
  address: RaAddressType;
  property: {
    property_type_id: string;
    number_of_rooms?: number | null;
    number_of_bedrooms?: number | null;
    living_surface?: number | null;
    land_surface?: number | null;
    built_surface?: number | null;
    usable_surface?: number | null;
  };
  details: {
    purchase_price?: number;
    signature_date?: Date | string | null;
    seller_broker_id?: string;
    seller_broker_team_id?: string;
  };
  photos?: {
    uploadedImage?: { url: string };
    listingImage?: { bucket_name: string; file_name: string };
  };
  selectedListingId?: number;
};

export const buildCreateTransactionInput = (
  data: TransactionInputData,
): Property_Transactions_Insert_Input => {
  const propertyData: Properties_Insert_Input = {
    ...addressToPropertyData(data.address),
    property_type_id: data.property.property_type_id,
    number_of_rooms: data.property.number_of_rooms || undefined,
    number_of_bedrooms: data.property.number_of_bedrooms || undefined,
    living_surface: data.property.living_surface || undefined,
    land_surface: data.property.land_surface || undefined,
    built_surface: data.property.built_surface || undefined,
    usable_surface: data.property.usable_surface || undefined,
  };

  if (data.photos?.uploadedImage) {
    propertyData.property_images = {
      data: [
        {
          image: {
            data: { url: data.photos.uploadedImage.url },
          },
        },
      ],
    };
  } else if (data.photos?.listingImage) {
    // Use listing image as fallback when no uploaded image is provided
    // Construct URL from bucket_name and file_name (same pattern as TeamOnlineListingsSection.tsx)
    const listingImageUrl = `https://storage.googleapis.com/${data.photos.listingImage.bucket_name}/${data.photos.listingImage.file_name}`;
    propertyData.property_images = {
      data: [
        {
          image: {
            data: { url: listingImageUrl },
          },
        },
      ],
    };
  }

  const transaction: Property_Transactions_Insert_Input = {
    property: {
      data: propertyData,
    },
    purchase_price: data.details.purchase_price,
    seller_broker_id: data.details.seller_broker_id,
    seller_broker_team_id: data.details.seller_broker_team_id,
    signature_date: data.details.signature_date,
  };

  if (data.selectedListingId) {
    transaction.property_transactions_listings = {
      data: [{ listing_id: data.selectedListingId }],
    };
  }

  return transaction;
};

export type BrokerTeams = {
  default_team_id?: string | null;
  teams_users: { team_id: string }[];
};

/**
 * Team to put on a transaction after its broker changed: the current team is
 * kept when the broker is an active member of it (picked from that team's
 * list), otherwise the broker's default team. `undefined` = keep as is.
 */
export const brokerTeamToFollow = (
  broker: BrokerTeams | null | undefined,
  currentTeamId: string | null | undefined,
): string | undefined => {
  if (broker == null) {
    return undefined;
  }
  if (
    currentTeamId != null &&
    broker.teams_users.some(tu => tu.team_id === currentTeamId)
  ) {
    return undefined;
  }
  return broker.default_team_id ?? undefined;
};

/**
 * A non-admin with at most one active team has no seller team to choose:
 * Hasura only returns their own teams.
 */
export const isSellerTeamLocked = (
  me:
    | { is_admin?: boolean | null; teams?: unknown[] | null }
    | null
    | undefined,
): boolean => me?.is_admin !== true && (me?.teams?.length ?? 0) <= 1;
